import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../api/admin.api';
import { carouselAPI } from '../../api/carousel.api';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { getUploadErrorMessage, prepareImageForUpload } from '../../utils/imageUpload';
import './Carousels.css';

const DEFAULT_BUTTON = {
  label: '',
  href: '/products',
};

const createDefaultButton = () => ({ ...DEFAULT_BUTTON });

const createDefaultForm = () => ({
  title: '',
  subtitle: '',
  showButton: true,
  cta: '',
  href: '/products',
  buttons: [createDefaultButton()],
  theme: 'purple',
  imageUrl: '',
  placement: 'brand-logo',
});

const getCarouselItems = (response) => (
  Array.isArray(response?.data) ? response.data : []
);

const getCarouselButtons = (carousel = {}) => {
  const buttons = Array.isArray(carousel.buttons)
    ? carousel.buttons
      .map((button) => ({
        label: String(button.label || button.text || button.cta || '').trim(),
        href: String(button.href || button.link || '/products').trim() || '/products',
      }))
      .filter((button) => button.label)
    : [];

  if (buttons.length) {
    return buttons;
  }

  return carousel.showButton !== false && carousel.cta
    ? [{ label: carousel.cta, href: carousel.href || '/products' }]
    : [];
};

const Carousels = () => {
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(createDefaultForm);

  useEffect(() => {
    loadCarousels();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormData(createDefaultForm());
  };

  const loadCarousels = async () => {
    try {
      setLoading(true);
      const response = await carouselAPI.getAll();
      setCarousels(getCarouselItems(response));
    } catch (error) {
      console.error('Error loading carousels:', error);
      alert('Failed to load carousels');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;
    setFormData((current) => {
      if (name === 'showButton') {
        return {
          ...current,
          showButton: checked,
          buttons: checked && current.buttons.length === 0 ? [createDefaultButton()] : current.buttons,
        };
      }

      return {
        ...current,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  };

  const handleButtonChange = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      buttons: current.buttons.map((button, buttonIndex) => (
        buttonIndex === index ? { ...button, [field]: value } : button
      )),
    }));
  };

  const handleAddButton = () => {
    setFormData((current) => ({
      ...current,
      showButton: true,
      buttons: [...current.buttons, createDefaultButton()],
    }));
  };

  const handleRemoveButton = (index) => {
    setFormData((current) => {
      const buttons = current.buttons.filter((_, buttonIndex) => buttonIndex !== index);

      return {
        ...current,
        showButton: buttons.length > 0 ? current.showButton : false,
        buttons: buttons.length ? buttons : [createDefaultButton()],
      };
    });
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    setUploading(true);

    try {
      const uploadPayload = await prepareImageForUpload(file);
      const { url, absoluteUrl } = await adminAPI.uploadImage(uploadPayload);
      setFormData((current) => ({
        ...current,
        imageUrl: url || absoluteUrl,
      }));
    } catch (error) {
      console.error('Carousel image upload failed:', error);
      alert(getUploadErrorMessage(error, 'Carousel image upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.title || !formData.theme || !formData.imageUrl) {
      alert('Please fill in card text, theme, and photo.');
      return;
    }

    const buttons = formData.showButton
      ? formData.buttons
        .map((button) => ({
          label: String(button.label || '').trim(),
          href: String(button.href || '/products').trim() || '/products',
        }))
        .filter((button) => button.label)
      : [];
    const primaryButton = buttons[0] || {};

    const payload = {
      ...formData,
      buttons,
      cta: primaryButton.label || '',
      href: primaryButton.href || '',
      showButton: buttons.length > 0,
      placement: 'brand-logo',
    };

    try {
      if (editingId) {
        await carouselAPI.update(editingId, payload);
        alert('Carousel updated successfully');
      } else {
        await carouselAPI.create(payload);
        alert('Carousel created successfully');
      }

      resetForm();
      loadCarousels();
    } catch (error) {
      console.error('Error saving carousel:', error);
      alert('Failed to save carousel');
    }
  };

  const handleEdit = (carousel) => {
    const buttons = getCarouselButtons(carousel);

    setEditingId(carousel.id);
    setFormData({
      title: carousel.title || '',
      subtitle: carousel.subtitle || '',
      showButton: carousel.showButton !== false && buttons.length > 0,
      cta: buttons[0]?.label || '',
      href: buttons[0]?.href || '/products',
      buttons: buttons.length ? buttons : [createDefaultButton()],
      theme: carousel.theme || 'purple',
      imageUrl: carousel.imageUrl || '',
      placement: carousel.placement || 'brand-logo',
    });
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= carousels.length) {
      return;
    }

    const reordered = [...carousels];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    try {
      const response = await carouselAPI.reorder(reordered);
      setCarousels(getCarouselItems(response));
    } catch (error) {
      console.error('Error reordering carousels:', error);
      alert('Failed to reorder carousel cards');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carousel card?')) {
      return;
    }

    try {
      await carouselAPI.delete(id);
      alert('Carousel deleted successfully');
      loadCarousels();
    } catch (error) {
      console.error('Error deleting carousel:', error);
      alert('Failed to delete carousel');
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="carousels-page">
      <div className="carousels-container">
        <h1>Manage Home Carousel</h1>

        <form onSubmit={handleSubmit} className="carousel-form">
          <h2>{editingId ? 'Edit Carousel Card' : 'Add New Carousel Card'}</h2>

          <div className="form-group">
            <label htmlFor="title">Card text *</label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., GUCCI or New gift collection"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subtitle">Small text</label>
            <input
              id="subtitle"
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleInputChange}
              placeholder="e.g., Limited offer or category note"
            />
          </div>

          <div className="form-group form-group--checkbox">
            <label>
              <input
                type="checkbox"
                name="showButton"
                checked={formData.showButton}
                onChange={handleInputChange}
              />
              <span>Show button on this card</span>
            </label>
          </div>

          {formData.showButton && (
            <div className="form-group">
              <div className="carousel-buttons-editor__header">
                <label>Card buttons</label>
                <button className="carousel-add-button" type="button" onClick={handleAddButton}>
                  Add extra button
                </button>
              </div>

              <div className="carousel-buttons-editor">
                {formData.buttons.map((button, index) => (
                  <div className="carousel-button-row" key={`carousel-button-${index}`}>
                    <label htmlFor={`carousel-button-label-${index}`}>
                      <span>Button text</span>
                      <input
                        id={`carousel-button-label-${index}`}
                        type="text"
                        value={button.label}
                        onChange={(event) => handleButtonChange(index, 'label', event.target.value)}
                        placeholder="e.g., Shop now"
                      />
                    </label>
                    <label htmlFor={`carousel-button-link-${index}`}>
                      <span>Button link</span>
                      <input
                        id={`carousel-button-link-${index}`}
                        type="text"
                        value={button.href}
                        onChange={(event) => handleButtonChange(index, 'href', event.target.value)}
                        placeholder="e.g., /products?category=watches"
                      />
                    </label>
                    <button className="carousel-remove-button" type="button" onClick={() => handleRemoveButton(index)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="theme">Theme *</label>
            <select
              id="theme"
              name="theme"
              value={formData.theme}
              onChange={handleInputChange}
              required
            >
              <option value="purple">Purple</option>
              <option value="pink">Pink</option>
              <option value="yellow">Yellow</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="imageUpload">Photo *</label>
            <label className="carousel-upload">
              <input
                id="imageUpload"
                type="file"
                accept="image/*"
                onChange={(event) => {
                  handleImageUpload(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
              <span>{uploading ? 'Uploading...' : 'Upload photo'}</span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Photo URL</label>
            <input
              id="imageUrl"
              type="text"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleInputChange}
              placeholder="/uploads/image.jpg or https://example.com/image.jpg"
            />
            {formData.imageUrl && (
              <div className="image-preview">
                <img src={formData.imageUrl} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-actions">
            <Button type="submit" disabled={uploading}>
              {editingId ? 'Update Carousel' : 'Add Carousel'}
            </Button>
            {editingId && <Button type="button" onClick={resetForm} variant="secondary">Cancel</Button>}
          </div>
        </form>

        <div className="carousels-list">
          <h2>Current Carousel Cards ({carousels.length})</h2>
          {carousels.length === 0 ? (
            <p className="empty-state">No carousels found</p>
          ) : (
            <div className="carousel-grid-admin">
              {carousels.map((carousel, index) => {
                const buttonCount = carousel.showButton === false ? 0 : getCarouselButtons(carousel).length;

                return (
                  <div key={carousel.id} className="carousel-item">
                    <div className="carousel-item__image">
                      <img src={carousel.imageUrl} alt={carousel.title} />
                    </div>
                    <div className="carousel-item__content">
                      <h4>{carousel.title}</h4>
                      {carousel.subtitle && <p>{carousel.subtitle}</p>}
                      <div className="carousel-item__meta">
                        <span className={`theme-badge theme-badge--${carousel.theme}`}>{carousel.theme}</span>
                        <span className="order-badge">Order: {carousel.order}</span>
                        <span className="order-badge">
                          {buttonCount > 0 ? `${buttonCount} button${buttonCount === 1 ? '' : 's'}` : 'No button'}
                        </span>
                      </div>
                    </div>
                    <div className="carousel-item__actions">
                      <button className="btn-move" type="button" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                        Up
                      </button>
                      <button className="btn-move" type="button" onClick={() => handleMove(index, 1)} disabled={index === carousels.length - 1}>
                        Down
                      </button>
                      <button className="btn-edit" type="button" onClick={() => handleEdit(carousel)}>
                        Edit
                      </button>
                      <button className="btn-delete" type="button" onClick={() => handleDelete(carousel.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Carousels;
